import { useEffect, useState } from "react";
import { ExternalLink, Scale, ListChecks, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageTitle } from "@/components/Layout";

const Block = ({ icon: Icon, title, color, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <p className={`flex items-center gap-2 text-sm font-bold ${color}`}><Icon className="h-4 w-4" />{title}</p>
    {children}
  </div>
);

export default function Compliance() {
  const { t, user } = useApp();
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState("");
  useEffect(() => { api.get("/compliance").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => {
    if (!rows?.length || sel) return;
    const ct = (user?.customer_type || "").toLowerCase();
    const guess = rows.find((s) => (ct.includes("hotel") && s.section_id === "hotel") || (/(restaurant|caf|cater)/.test(ct) && s.section_id === "restaurant")
      || (ct.includes("office") && s.section_id === "office") || (ct === "household" && s.section_id === "household"));
    setSel((guess || rows[0]).section_id);
  }, [rows, user, sel]);
  const s = rows?.find((x) => x.section_id === sel);
  return (
    <div className="space-y-4">
      <PageTitle sub={t("What the rules ask of you, the fines for not following them, and how Kindsaver helps. Always check the linked official source.")}>{t("What your business must do")}</PageTitle>
      {rows && !rows.length && <Card data-testid="compliance-empty">{t("The compliance guide is being reviewed by our team. Please check back soon.")}</Card>}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {rows?.map((x) => (
          <button key={x.section_id} onClick={() => setSel(x.section_id)} data-testid={`compliance-tab-${x.section_id}`}
            className={`h-9 shrink-0 rounded-full px-4 text-sm font-semibold ${sel === x.section_id ? "bg-[#0D3B2B] text-white" : "border border-[#D2E0D8] bg-white text-[#4A6358]"}`}>{t(x.audience)}</button>
        ))}
      </div>
      {s && (
        <Card className="ks-rise space-y-5" data-testid={`compliance-section-${s.section_id}`}>
          <h2 className="font-display text-xl font-bold text-[#0D3B2B]">{t(s.title)}</h2>
          <Block icon={ListChecks} title={t("Your duties")} color="text-[#0D3B2B]" testid="compliance-duties">
            <ul className="ml-5 list-disc space-y-1 text-sm text-[#4A6358]">{s.duties.map((d) => <li key={d}>{d}</li>)}</ul>
          </Block>
          <Block icon={Scale} title={t("Fines & penalties")} color="text-[#E05C3A]" testid="compliance-fines">
            {s.fines.map((f, i) => (
              <div key={i} className="rounded-xl bg-[#FDF0EC] p-3 text-sm text-[#0D3B2B]">
                <p>{f.text}</p>
                {f.source_url && <a href={f.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#E05C3A] underline" data-testid={`compliance-source-${i}`}>{f.source_title || t("Source")}<ExternalLink className="h-3 w-3" /></a>}
              </div>
            ))}
          </Block>
          <Block icon={Sparkles} title={t("How Kindsaver helps")} color="text-[#2E9E6B]" testid="compliance-help">
            <ul className="ml-5 list-disc space-y-1 text-sm text-[#4A6358]">{s.help.map((d) => <li key={d}>{d}</li>)}</ul>
          </Block>
          <p className="text-[11px] text-[#7B968B]">{t("Last reviewed")}: {s.updated_at?.slice(0, 10)}. {t("This is general guidance, not legal advice.")}</p>
        </Card>
      )}
    </div>
  );
}
